import {
  Button,
  ButtonGroup,
  Card,
  ErrorSummary,
  ExternalLink,
  Hero,
  Notification,
  Page,
  PersonalDataMask,
  RadioButtonGroup,
  useAccessTokenManagement,
  useBrand
} from '@hbo/ui-servicing-react-components';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { Modal } from '@zambezi/sdk/modal';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { useDataLayer } from 'react-common-gdlv2';
import { Helmet } from 'react-helmet-async';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Navigate, useBlocker, useLocation, useNavigate } from 'react-router-dom';
import { AuthenticationService } from 'services/authentication';
import { OneTimePasscodeService } from 'services/oneTimePasscode';
import { RoutePath } from 'types/global';
import { getExternalLinkElement, getInternalLinkElement } from 'utils/analytics';
import { schema } from './config';

interface Schema {
  isMobileNumberConfirmed?: boolean;
}

type LocationState = AuthenticationService.Payload & AuthenticationService.Response;

const ConfirmMobileNumber: React.FC = () => {
  const { brand } = useBrand();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // const { track } = useDataLayer();

  const [isFraudModalActive, setIsFraudModalActive] = useState(false);
  let isBlocked = true;

  const { isAuthenticated } = useAccessTokenManagement();

  /** Block navigation to any other page */
  useBlocker(() => {
    return isBlocked;
  });

  const state = location.state as LocationState;

  const {
    mutate,
    isPending: isLoading,
    isError,
    error
  } = useMutation<
    OneTimePasscodeService.GenerateResponse,
    AxiosError,
    OneTimePasscodeService.GeneratePayload
  >({
    mutationFn: (data) => OneTimePasscodeService.generate(data),
    onSuccess: ({ customStatusCode }) => {
      isBlocked = false;

      if (customStatusCode === OneTimePasscodeService.CustomStatusCode.AccountLocked) {
        return navigate(RoutePath.Locked, { replace: true, state: { reason: 'OTP' } });
      }

      if (customStatusCode === OneTimePasscodeService.CustomStatusCode.ServiceDown) {
        return navigate(RoutePath.OneTimePasscodeServiceDown, { replace: true });
      }

      navigate(RoutePath.OneTimePasscode, { state });
    }
  });

  const { control, handleSubmit } = useForm({
    mode: 'onBlur',
    //@ts-ignore
    resolver: yupResolver(schema)
  });

  const onSubmit: SubmitHandler<Schema> = (data) => {
    isBlocked = false;

    if (!data.isMobileNumberConfirmed) {
      // track('EXCEPTION: Reason - mobile number not confirmed', null, 'Error');
      return navigate(RoutePath.Error, { replace: true });
    }

    return mutate({
      accountNumber: state.accountNumber,
      postcode: state.postcode,
      dob: state.dob,
      surname: state.surname
    });
  };

  if (!state?.mobileNumber) {
    return <Navigate to={RoutePath.FourQuestionVerification} replace />;
  }

  if (isAuthenticated) {
    return <Navigate to={RoutePath.CaseTracking} replace/>;
  }

  return (
    <>
      <Helmet>
        <title>{t('confirmMobileNumber.tabTitle')}</title>
      </Helmet>
      <Hero>
        <Hero.PrimaryContent title={t('confirmMobileNumber.title')} />
      </Hero>
      <Page>
        <Page.Content>
          <Notification variant="info" title={t('confirmMobileNumber.notification.fraud.title')}>
            <span className="sr-only">{t('generic.screenReaderInfo')}</span>
            <p>{t('confirmMobileNumber.notification.fraud.line1')}</p>
            <Button variant="link" onClick={() => setIsFraudModalActive(true)}>
              {t('confirmMobileNumber.notification.fraud.line2')}
            </Button>
          </Notification>
        </Page.Content>
        <Page.Content>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Card>
              <Card.Header>{t('confirmMobileNumber.heading1')}</Card.Header>
              <Card.Body>
                {isError &&
                <ErrorSummary title="">
                  <span className="sr-only">{t('generic.screenReaderError')}</span>
                  {error.message}
                </ErrorSummary>}
                <p>{t('confirmMobileNumber.line1')}</p>

                <p className="mss-text-emphasis">
                  <PersonalDataMask>{state.mobileNumber}</PersonalDataMask>
                </p>
                <Controller
                  render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => (
                    <RadioButtonGroup
                      label={t('confirmMobileNumber.options.label')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      options={[
                        {
                          label: t('confirmMobileNumber.options.option1'),
                          value: true
                        },
                        {
                          label: t('confirmMobileNumber.options.option2'),
                          value: false
                        }
                      ]}
                      ref={ref}
                      error={error?.message}
                      hideLabel
                    />
                  )}
                  name="isMobileNumberConfirmed"
                  control={control}
                />
              </Card.Body>
            </Card>
            <ButtonGroup>
              <Button
                onClick={(event: React.MouseEvent<Element, MouseEvent>) => {
                  const pageName = t('confirmMobileNumber.tabTitle');
                  // track(
                  //   `CLICK: ${pageName} - ${t('generic.cancel')} - Button`,
                  //   getInternalLinkElement(event),
                  //   pageName
                  // );
                  isBlocked = false;
                  navigate(RoutePath.FourQuestionVerification, { replace: true });
                }}
                variant="secondary"
                disabled={isLoading}
                data-cy="cancel-button"
              >
                {t('generic.cancel')}
              </Button>
              <Button
                type="submit"
                data-cy="continue-button"
                isLoading={isLoading}
                onClick={(event: React.MouseEvent<Element, MouseEvent>) => {
                  const pageName = t('confirmMobileNumber.tabTitle');
                  // track(
                  //   `CLICK: ${pageName} - ${t('generic.continue')} - Button`,
                  //   getInternalLinkElement(event),
                  //   pageName
                  // );
                }}
              >
                {t('generic.continue')}
              </Button>
            </ButtonGroup>
          </form>
        </Page.Content>
      </Page>
      {isFraudModalActive && (
        <Modal
          confirm={t('confirmMobileNumber.modal.fraud.action') as string}
          onConfirm={() => setIsFraudModalActive(false)}
          onCancel={() => setIsFraudModalActive(false)}
          title={t('confirmMobileNumber.modal.fraud.title')}
          open={true}
          isClosable={true}
        >
          <p>{t('confirmMobileNumber.modal.fraud.line1')}</p>
          <p>{t('confirmMobileNumber.modal.fraud.line2')}</p>
          <p>
            <Trans
              i18nKey="confirmMobileNumber.modal.fraud.line3"
              components={[
                // @ts-expect-error can't pass children
                <ExternalLink
                  href={t(`confirmMobileNumber.modal.fraud.link.${brand}`)}
                  onClick={(event: React.MouseEvent<Element, MouseEvent>) => {
                    const pageName = t('confirmMobileNumber.tabTitle');
                    // track(
                    //   `CLICK: ${pageName} - Update your number online - Link`,
                    //   getExternalLinkElement(event),
                    //   pageName
                    // );
                  }}
                />
              ]}
            />
          </p>
        </Modal>
      )}
    </>
  );
};

export default ConfirmMobileNumber;
